/**
 *  ・クラス名
 *    計算用共通モデル
 *  ・概要
 *    画面共通で使用可能な計算処理を記述
 *  ・更新履歴
 *    2021/09/29 新規作成 小林
 */

export interface TableData {
  tableData: Array<any>;
  itemName: string;
  sumItem?: {
    display_id: string;
    targetName: string;
  };
}

// 端数処理の際に使用する定数(100円未満を端数とする場合)
const FractionDefaultValue = 100;

// 端数切捨て桁定数(100円未満を端数とする場合)
const FractionRoundingDefaultDigit = 2;

export default class CalculationModel {
  /**
   * 複数のテーブルデータから合計値を出力
   * 指定した複数のデータテーブルのデータから
   * 指定した項目で集計を行い、合計値を返す
   * @module totalValues
   * @param {any} tableDatas - 集計する複数のデータテーブルと処理する項目名の配列
   * @return {number} sum - 合計値
   * tableDatasの中身
   * {
   *   tableDatas：集計するデータテーブル、
   *   itemName：処理する項目名(display_idなど)
   *   sumItem：{ 特定の区分、種別などで使用する内容(値引など)}
   * }
   *
   * sumItemの中身
   * {
   *   display_id：判別に使用する項目(display_idなど)
   *   targetName：抽出に使う特定文言(値引など)
   * }
   */
  public totalValues(tableDatas: Array<TableData>) {
    let sum = 0;
    for (const index in tableDatas) {
      const tableData = tableDatas[index].tableData;
      const itemName = tableDatas[index].itemName;
      for (const dataIndex in tableData) {
        if (
          tableDatas[index].sumItem === undefined ||
          tableDatas[index].sumItem!.targetName === '' ||
          tableData[dataIndex][tableDatas[index].sumItem!.display_id] ===
            tableDatas[index].sumItem!.targetName
        ) {
          sum += tableData[dataIndex][itemName];
        }
      }
    }
    return sum;
  }

  /**
   * 営業利益
   * 売上高-売上原価で売上総利益を計算し、
   * 売上総利益- 販売費および一般管理費で営業利益を返す
   * @module operatingIncome
   * @param {number} amountSales - 売上高
   * @param {number} costSales - 売上原価
   * @param {number} optionSales - 販売費および一般管理費
   * @return {number} operatingIncome - 営業利益
   */
  public operatingIncome(amountSales = 0, costSales = 0, optionSales = 0) {
    const profit = amountSales - costSales;
    const operatingIncome = profit - optionSales;
    return operatingIncome;
  }

  /**
   * 利益率
   * 営業利益、販売価格で利益率を計算し、％表記にして返す。
   * @module profitRate
   * @param {number} profit - 利益(営業利益など)
   * @param {number} price - 売上(販売価格など)
   * @param {object} roundingType - 端数処理の種類
   * @return {string} profitRate - 利益率(%付き)
   */
  public profitRate(profit: number, price: number, roundingType: any) {
    let profitRate = (profit / price) * 100;
    if (isNaN(profitRate) || isFinite(profitRate) === false || '-Infinity' === String(profitRate)) {
      profitRate = 0;
    } else {
      profitRate = this.roundingProcess(profitRate, roundingType);
    }
    return String(profitRate) + '%';
  }

  /**
   * 税額計算(消費税のみ、消費税込)
   * 指定された税率と税別の合計値から消費税のみ、消費税込、を返す
   * @module taxAmount
   * @param {number} tax - 税率
   * @param {number} sum - 税別の合計値
   * @param {string} type - 消費税のみ、消費税込、
   * @param {object} roundingType - 端数処理の種類
   * roundingTypeの中身
   * {
   *   calculationType：端数処理の種類
   *   digit: 桁数
   * }
   * @return {number} num - 税額計算
   */
  public taxAmount(tax: number, sum: number, type: string, roundingType: any) {
    let num = 0;
    switch (type) {
      case 'taxOnly': {
        // 消費税のみ
        if (sum !== 0 && tax !== 0) {
          num = sum * (tax / 100);
        }
        num = this.roundingProcess(num, roundingType);
        break;
      }
      case 'taxIncluded': {
        // 消費税込
        if (sum !== 0 && tax !== 0) {
          num = sum * (tax / 100 + 1);
        }
        num = this.roundingProcess(num, roundingType);
        break;
      }
    }
    if (isNaN(num) || isFinite(num) === false || '-Infinity' === String(num)) {
      num = 0;
    }
    return num;
  }

  /**
   * 月額リース料
   * 税抜合計額と料率から月額リース料を整数値で返却
   * @module monthlyLeaseCost
   * @param {number} sum - 税抜合計額
   * @param {number} rate - リース料率
   * @return {number} monthlyLeaseCost - 月額リース料
   */
  public monthlyLeaseCost(sum: number, rate: number) {
    const num = this.roundingProcess(sum * (rate / 100), { calculationType: 'floor' });
    return num;
  }

  /**
   * 調整金額
   * 料率と月額リース料からあるべき金額を算出し、税抜合計額との差分を調整金額として返却
   * @module adjustmentAmount
   * @param {number} sum - 税抜合計額
   * @param {number} rate - リース料率
   * @param {number} cost - 月額リース料
   * @return {number} 調整金額
   */
  public adjustmentAmount(sum: number, rate: number, cost: number) {
    let num = this.fractionRounding(cost / (rate / 100));
    num = num - sum;
    return num;
  }

  /**
   * 端数を返す
   * @module fractionProcess
   * @param {number} value - 元になる値
   * @param {number} fractionValue - 端数算出に使用する値
   * @return {number} 端数
   */
  public fractionProcess(value: number, fractionValue: number = FractionDefaultValue) {
    const fractionNum = value % fractionValue;
    return fractionNum;
  }

  /**
   * 端数切捨て
   * 元になる値を切捨て桁数で切捨てた値を返却
   * @module fractionRounding
   * @param {number} value - 元になる値
   * @param {number} roundingDigit - 切捨て桁数
   * @return {number} 端数切捨て値
   */
  public fractionRounding(value: number, roundingDigit: number = FractionRoundingDefaultDigit) {
    const num = this.roundingProcess(value, { calculationType: 'floor', digit: roundingDigit });
    return num;
  }

  /**
   * 指定演算子計算
   * 配列数分、指定された四則演算子で計算を行う
   * ※単項マイナスなどの際に指定した四則演算子で計算
   * @module specifiedOperatorCalculation
   * @param {number} calculationItems - 計算に必要な値と演算子を格納する配列
   * @return {number} num - 計算した値
   * calculationItemsの中身
   * [
   *     {
   *         value: 計算に使用する値
   *         calculationType：前配列までの計算値との計算に使用する四則演算子(+,-,*,/,%など)
   *     }
   *   ]
   */
  public specifiedOperatorCalculation(calculationItems: Array<any>) {
    let num = 0;
    for (const index in calculationItems) {
      switch (calculationItems[index].calculationType) {
        case '+': {
          num = num + calculationItems[index].value;
          break;
        }
        case '-': {
          num = num - calculationItems[index].value;
          break;
        }
        case '*': {
          num = num * calculationItems[index].value;
          break;
        }
        case '/': {
          num = num / calculationItems[index].value;
          break;
        }
        case '%': {
          num = num % calculationItems[index].value;
          break;
        }
        default: {
          break;
        }
      }
    }
    if (isNaN(num) || isFinite(num) === false || '-Infinity' === String(num)) {
      num = 0;
    }
    return num;
  }

  /**
   * 端数処理
   * 端数処理(四捨五入、切捨て又は切上げ)を行う
   * @module roundingProcess
   * @param {number} value - 端数処理する値
   * @param {object} roundingItem - 端数処理の種類、桁数などの情報
   * {
   *     calculationType 端数処理の種類
   *     digit: 桁数
   * }
   * @return {number} num - 端数処理した値
   */
  public roundingProcess(value: any, roundingItem: any) {
    let num = 0;
    switch (roundingItem.calculationType) {
      case 'decimalRound': {
        // 少数の四捨五入
        const rounding = 10 ** roundingItem.digit;
        num = Math.round(value * rounding) / rounding;
        break;
      }
      case 'integerRound': {
        // 整数の四捨五入
        const rounding = 10 ** roundingItem.digit;
        num = Math.round(value / rounding) * rounding;
        break;
      }
      case 'floor': {
        // 切り捨て
        if (roundingItem.digit === undefined) {
          num = Math.floor(value);
        } else {
          const rounding = 10 ** roundingItem.digit;
          num = Math.floor(value / rounding) * rounding;
        }
        break;
      }
      case 'ceil': {
        // 切り上げ
        num = Math.ceil(value);
        break;
      }
    }
    return num;
  }
}
